import React, { useState, useCallback } from 'react';
import { FileText, Link, Package, Plus, User, Lock, X, Loader2 } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import MappingAutocomplete from '../common/MappingAutocomplete';
import useVendorNames from '../../core/ui/useVendorNames';

/**
 * Bezugsquelle-Tab einer BANF-Position.
 *
 * - Wunschlieferant / Fester Lieferant: Live-Wertehilfe (LFA1) + Namensauflösung unter dem Feld.
 * - "Bezugsquelle zuordnen" (ME56/ME57-artig): holt aktive Infosätze des Materials über die
 *   Adapter-Wertehilfe `info_record` (Contract §7.4 Punkt 15) und füllt bei Auswahl
 *   Infosatz/Lieferant/Preis/Preiseinheit/Währung in die Position. Solange der Adapter die
 *   Hilfe noch nicht liefert, erscheint ein sauberer Hinweis statt eines toten Buttons.
 * - Infosatz-Feld: bekannte INFNR eintippen -> onBlur-Lookup -> gleiche Übernahme.
 */
const SourceTab = ({ item, onUpdate, themeColors, user }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  // Dialog "Bezugsquelle zuordnen": null | { loading, rows, unavailable }
  const [sourceDialog, setSourceDialog] = useState(null);

  const token = user?.token;

  const fetchValueHelp = useCallback(async (helpId, params) => {
    const qs = new URLSearchParams(params);
    const res = await fetch(`/api/value-help/${helpId}?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return { ok: false, rows: [] };
    const data = await res.json();
    return { ok: Boolean(data.ok), rows: Array.isArray(data.rows) ? data.rows : [] };
  }, [token]);

  // LIFNR -> Klartextname (gemeinsamer Hook, gleiches Prinzip wie im Allgemein-Tab).
  const vendorNameFor = useVendorNames([item.preferred_vendor, item.fixed_vendor], token);

  // Infosatz-Zeile in die Position übernehmen. Feldnamen defensiv (Contract §7.4 Punkt 15:
  // {infnr, lifnr, name/text, netpr, peinh, waers}); nur befüllte Werte übernehmen.
  const applySource = (row) => {
    const infnr = row.infnr || row.code;
    if (infnr) onUpdate(item.id, 'info_record', String(infnr));
    if (row.lifnr) onUpdate(item.id, 'fixed_vendor', String(row.lifnr).replace(/^0+/, ''));
    if (row.netpr !== undefined && row.netpr !== null && row.netpr !== '') {
      onUpdate(item.id, 'price', String(row.netpr));
    }
    if (row.peinh) onUpdate(item.id, 'price_unit', parseInt(row.peinh, 10) || 1);
    if (row.waers) onUpdate(item.id, 'currency', row.waers);
    setSourceDialog(null);
  };

  // ME56/ME57-artig: aktive Infosätze zum Material laden.
  const handleBezugsquelleZuordnen = async () => {
    const matnr = String(item.material || '').trim();
    if (!matnr) {
      setSourceDialog({ loading: false, rows: [], unavailable: false, noMaterial: true });
      return;
    }
    setSourceDialog({ loading: true, rows: [], unavailable: false });
    try {
      const params = { matnr, limit: '20' };
      if (item.plant) params.werks = item.plant;
      const { ok, rows } = await fetchValueHelp('info_record', params);
      setSourceDialog({ loading: false, rows, unavailable: !ok });
    } catch {
      setSourceDialog({ loading: false, rows: [], unavailable: true });
    }
  };

  // Bekannte INFNR direkt eingetippt -> onBlur nachschlagen und Felder füllen.
  const handleInfoRecordBlur = async () => {
    const infnr = String(item.info_record || '').trim();
    if (!/^\d{5,}$/.test(infnr) || !token) return;
    try {
      const { rows } = await fetchValueHelp('info_record', { q: infnr, limit: '3' });
      const hit = rows.find((r) => String(r.infnr || r.code).replace(/^0+/, '') === infnr.replace(/^0+/, ''));
      if (hit) applySource(hit);
    } catch {
      // Hilfe (noch) nicht verfügbar -> Freitext bleibt einfach stehen.
    }
  };

  const glassInput = `relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-gray-800 placeholder-gray-400'}`;
  const glassBg = `absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`;
  const vendorHint = (value) => {
    const name = vendorNameFor(value);
    if (!name) return null;
    return (
      <p className={`mt-1 text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{name}</p>
    );
  };

  return (
    <div className="space-y-6">
      {/* Fields Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {/* Vertrag */}
      <div>
        <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
          <FileText className="w-4 h-4 mr-1.5" />
          {t('banf.source.contract')}
        </label>
        <div className="relative">
          <div className={glassBg}></div>
          <input
            type="text"
            value={item.contract || ''}
            onChange={(e) => onUpdate(item.id, 'contract', e.target.value)}
            className={glassInput}
            placeholder={t('banf.source.contractPlaceholder')}
          />
        </div>
      </div>

      {/* Infosatz — bekannte INFNR füllt die Bezugsquellen-Felder (Lookup onBlur) */}
      <div>
        <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
          <Link className="w-4 h-4 mr-1.5" />
          {t('banf.source.infoRecord')}
        </label>
        <div className="relative">
          <div className={glassBg}></div>
          <input
            type="text"
            value={item.info_record || ''}
            onChange={(e) => onUpdate(item.id, 'info_record', e.target.value)}
            onBlur={handleInfoRecordBlur}
            className={glassInput}
            placeholder={t('banf.source.infoRecordPlaceholder')}
          />
        </div>
      </div>

        {/* Lieferantenmat.-Nr. */}
        <div>
          <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
            <Package className="w-4 h-4 mr-1.5" />
            {t('banf.source.supplierMatNo')}
          </label>
          <div className="relative">
            <div className={glassBg}></div>
            <input
              type="text"
              value={item.vendor_material_number || ''}
              onChange={(e) => onUpdate(item.id, 'vendor_material_number', e.target.value)}
              className={glassInput}
              placeholder={t('banf.source.supplierMatNoPlaceholder')}
            />
          </div>
        </div>

        {/* Wunschlieferant — Live-Wertehilfe LFA1 + Namensauflösung */}
        <div>
          <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
            <User className="w-4 h-4 mr-1.5" />
            {t('banf.source.preferredVendor')}
          </label>
          <MappingAutocomplete
            helpId="vendor"
            minChars={2}
            value={item.preferred_vendor || ''}
            onChange={(v) => onUpdate(item.id, 'preferred_vendor', v)}
            placeholder={t('banf.source.preferredVendorPlaceholder')}
            token={token}
            isDarkMode={isDarkMode}
          />
          {vendorHint(item.preferred_vendor)}
        </div>

        {/* Fester Lieferant — Live-Wertehilfe LFA1 (EBAN.FLIEF) + Namensauflösung */}
        <div>
          <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
            <Lock className="w-4 h-4 mr-1.5" />
            {t('banf.source.fixedVendor')}
          </label>
          <MappingAutocomplete
            helpId="vendor"
            minChars={2}
            value={item.fixed_vendor || ''}
            onChange={(v) => onUpdate(item.id, 'fixed_vendor', v)}
            placeholder={t('banf.source.fixedVendorPlaceholder')}
            token={token}
            isDarkMode={isDarkMode}
          />
          {vendorHint(item.fixed_vendor)}
        </div>
      </div>

      {/* Button Section */}
      <div className="flex justify-start">
        <button
          onClick={handleBezugsquelleZuordnen}
          className="relative group/assign overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
          style={{
            background: isDarkMode
              ? 'linear-gradient(135deg, rgba(34,197,94,0.6) 0%, rgba(16,185,129,0.6) 50%, rgba(5,150,105,0.6) 100%)'
              : 'linear-gradient(135deg, rgba(34,197,94,0.9) 0%, rgba(16,185,129,0.9) 50%, rgba(5,150,105,0.9) 100%)',
            backdropFilter: isDarkMode ? 'blur(20px)' : 'blur(16px)',
            border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(255,255,255,0.6)',
            boxShadow: isDarkMode
              ? '0 8px 32px rgba(34,197,94,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              : '0 6px 24px rgba(34,197,94,0.25), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.05)'
          }}
        >
          <div
            className="absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/assign:opacity-100"
            style={{
              background: isDarkMode
                ? 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                : 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.2) 100%)'
            }}
          ></div>

          <div className="relative z-10 px-4 py-2 flex items-center font-medium text-white transition-all duration-300 drop-shadow-lg group-hover/assign:drop-shadow-xl">
            <Plus className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/assign:scale-110" />
            {t('banf.source.assignSource')}
          </div>
        </button>
      </div>

      {/* Dialog: aktive Infosätze des Materials (ME56/ME57-artige Bezugsquellenfindung) */}
      {sourceDialog && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSourceDialog(null)} />
          <div className={`relative w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl ${
            isDarkMode ? 'bg-gray-900/90 border border-white/20 backdrop-blur-xl' : 'bg-white border border-slate-200'
          }`}>
            <div className={`px-6 py-4 border-b flex items-center justify-between ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                {t('banf.source.assignTitle')}
                {item.material ? <span className={`ml-2 text-sm font-normal ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>({item.material})</span> : null}
              </h3>
              <button
                onClick={() => setSourceDialog(null)}
                className={`p-2 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-white/10 text-white/60' : 'hover:bg-gray-100 text-gray-400'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
              {sourceDialog.noMaterial ? (
                <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{t('banf.source.assignNoMaterial')}</p>
              ) : sourceDialog.loading ? (
                <div className="flex items-center space-x-2 py-4">
                  <Loader2 className={`w-4 h-4 animate-spin ${isDarkMode ? 'text-white/60' : 'text-slate-400'}`} />
                  <span className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>{t('banf.source.assignLoading')}</span>
                </div>
              ) : sourceDialog.rows.length === 0 ? (
                <p className={`text-sm ${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                  {sourceDialog.unavailable ? t('banf.source.assignUnavailable') : t('banf.source.assignEmpty')}
                </p>
              ) : (
                <div className="space-y-2">
                  {sourceDialog.rows.map((row, idx) => (
                    <button
                      key={`${row.infnr || row.code}-${idx}`}
                      type="button"
                      onClick={() => applySource(row)}
                      className={`w-full text-left px-4 py-3 rounded-2xl border transition-all duration-150 ${
                        isDarkMode
                          ? 'bg-gray-800/40 border-white/15 hover:bg-brand/20 text-white'
                          : 'bg-slate-50 border-slate-100 hover:bg-brand/5 text-slate-800'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-mono font-semibold text-sm text-brand">{row.infnr || row.code}</span>
                          <span className={`ml-3 text-sm ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>
                            {row.name || row.text || row.lifnr || ''}
                          </span>
                        </div>
                        {(row.netpr !== undefined && row.netpr !== null && row.netpr !== '') && (
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>
                            {row.netpr} {row.waers || ''}{row.peinh && parseInt(row.peinh, 10) !== 1 ? ` / ${row.peinh}` : ''}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SourceTab;
